<template>
    <div class="suspicious-transactions">
        <div class="d-flex justify-content-between align-items-center mb-3 row">
            <div class="col-md-10">
                <h4 class="mb-0">Suspicious Transactions</h4>
                <small class="text-muted"
                    >Monitor and investigate flagged transactions</small
                >
            </div>
        </div>

        <b-row class="mt-3">
            <b-col md="3" v-for="card in statCards" :key="card.key">
                <div
                    class="alert-card shadow-sm p-2 mb-3"
                    :class="card.cardClass"
                >
                    <div class="card-content">
                        <div class="icon-box" :class="card.iconClass">
                            <i :class="card.icon"></i>
                        </div>
                        <div class="card-text">
                            <div class="mb-1 main-title">{{ card.label }}</div>
                            <h5 class="m-0 font-weight-bold">
                                {{ dashboardStats[card.key] }}
                            </h5>
                        </div>
                    </div>
                </div>
            </b-col>
        </b-row>

        <div class="filter-box shadow-sm p-4 mb-3">
            <b-row class="align-items-center">
                <b-col md="6">
                    <div class="search-wrapper">
                        <i class="fa fa-search search-icon"></i>
                        <input
                            v-model="searchQuery"
                            type="text"
                            class="search-input form-control"
                            placeholder="Search by transaction ID, user, or seller..."
                        />
                    </div>
                </b-col>

                <b-col md="3">
                    <b-form-select
                        v-model="riskFilter"
                        :options="riskOptions"
                        class="custom-select-box"
                    ></b-form-select>
                </b-col>

                <b-col md="3">
                    <b-form-select
                        v-model="statusFilter"
                        :options="statusOptions"
                        class="custom-select-box"
                    ></b-form-select>
                </b-col>
            </b-row>
        </div>

        <b-card>
            <b-table
                :items="filteredTransactions"
                :fields="fields"
                :per-page="perPage"
                :current-page="currentPage"
                responsive
                bordered
                thead-class="header"
                class="transaction-table tableRadius"
            >
                <template #cell(risk)="data">
                    <span
                        class="risk-badge"
                        :class="
                            'risk-' +
                            data.item.risk.toLowerCase().replace('/', '')
                        "
                    >
                        {{ data.item.risk }}
                    </span>
                </template>

                <template #cell(flags)="data">
                    <span class="flag-icon"
                        ><i class="fa fa-exclamation-triangle text-warning"></i
                    ></span>
                    {{ data.item.flags }}
                </template>

                <template #cell(status)="data">
                    <span
                        class="status-badge"
                        :class="
                            'status-' +
                            data.item.status.toLowerCase().replace(' ', '')
                        "
                    >
                        {{ data.item.status }}
                    </span>
                </template>
            </b-table>
            <div class="pagination-wrapper">
                <b-pagination
                    v-model="currentPage"
                    :total-rows="filteredTransactions.length"
                    :per-page="perPage"
                    aria-controls="my-table"
                    prev-text="Prev"
                    next-text="Next"
                    hide-goto-end-buttons
                    class="justify-content-center justify-content-md-end"
                ></b-pagination>
            </div>
        </b-card>
    </div>
</template>

<script>
import moment from "moment";
export default {
    data() {
        return {
            searchQuery: "",
            riskFilter: "All",
            statusFilter: "All",
            currentPage: 1,
            perPage: 10,
            transactionData: [],
            riskOptions: ["All", "High", "Medium", "Low"],
            statusOptions: ["All", "Failed", "Confirmed"],
            fields: [
                "transaction_id",
                "user",
                "amount",
                "items",
                "risk",
                "time",
                "status",
            ],
            riskData: {},
        };
    },

    computed: {
        transactions() {
            if (!this.transactionData || !Array.isArray(this.transactionData)) {
                return [];
            }

            return this.transactionData.map((item) => {
                const formatDate = (dateString) => {
                    if (!dateString) return "N/A";
                    const date = new Date(dateString);
                    const options = {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                    };
                    return date.toLocaleDateString("en-US", options);
                };

                const formatAmount = (amount) => {
                    if (!amount || isNaN(amount)) return "$0.00";
                    const numAmount = parseFloat(amount);
                    if (isNaN(numAmount) || numAmount <= 0) return "$0.00";
                    return `$${numAmount.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}`;
                };

                const getUserName = () => {
                    if (this.isHashLike(item.customer_name)) {
                        return item.customer_email &&
                            !this.isHashLike(item.customer_email)
                            ? item.customer_email
                            : "N/A";
                    }
                    return item.customer_name || item.customer_email || "N/A";
                };

                const getSeller = () => {
                    return item.provider || item.market || "N/A";
                };

                const getFlags = () => {
                    const flags = [];
                    if (item.behavior_fraud) {
                        flags.push("Behavior fraud");
                    }
                    if (item.reason) {
                        flags.push(item.reason.replace(/_/g, " "));
                    }
                    if (
                        item.orderamount &&
                        parseFloat(item.orderamount) > 10000
                    ) {
                        flags.push("High-value");
                    }
                    return flags.length > 0 ? flags.join(", ") : "No flags";
                };

                const getStatus = () => {
                    if (item.orderstatus === "refund") {
                        return "Flagged";
                    }
                    if (item.event_status === "ABUSE") {
                        return "Under Review";
                    }
                    return (
                        item?.orderstatus.charAt(0).toUpperCase() +
                            item?.orderstatus.slice(1) || "Flagged"
                    );
                };

                const parsedAmount = parseFloat(item.orderamount);
                let computedRisk = "N/A";
                if (!isNaN(parsedAmount)) {
                    if (parsedAmount > 10000 && parsedAmount < 12000) {
                        computedRisk = "Low";
                    } else if (parsedAmount > 12000 && parsedAmount < 15000) {
                        computedRisk = "Medium";
                    } else if (parsedAmount > 20000) {
                        computedRisk = "High";
                    }
                }

                return {
                    transaction_id: `TXN-${item.fullfillmentid}`,
                    user: getUserName(),
                    seller: getSeller(),
                    amount: formatAmount(item.orderamount),
                    items: item.item_name ? 1 : "N/A",
                    risk: computedRisk,
                    flags: getFlags(),
                    time: formatDate(item.createdat),
                    status: getStatus(),
                    // Store original item for reference
                    originalItem: item,
                };
            });
        },
        riskComputedData() {
            if (!this.riskData || !Array.isArray(this.riskData)) {
                return [];
            }
            return this.riskData;
        },

        dashboardStats() {
            const total = this.transactions.length;
            const lowRisk = this.riskComputedData.find(
                (t) => t.amount_group === "10K to 12K",
            );
            const mediumRisk = this.riskComputedData.find(
                (t) => t.amount_group === "12K to 15K",
            );
            const highRisk = this.riskComputedData.find(
                (t) => t.amount_group === "above 15K",
            );

            return {
                totalFlagged: total,
                highRisk: highRisk?.total_orders || 0,
                mediumRisk: mediumRisk?.total_orders || 0,
                lowRisk: lowRisk?.total_orders || 0,
            };
        },

        statCards() {
            return [
                {
                    key: "totalFlagged",
                    label: "Total Flagged",
                    icon: "fa fa-flag",
                    cardClass: "total-card",
                    iconClass: "total-icon",
                },
                {
                    key: "highRisk",
                    label: "High Risk",
                    icon: "fa fa-exclamation-triangle",
                    cardClass: "critical-card",
                    iconClass: "critical-icon",
                },
                {
                    key: "mediumRisk",
                    label: "Medium Risk",
                    icon: "fa fa-shield",
                    cardClass: "active-card",
                    iconClass: "active-icon",
                },
                {
                    key: "lowRisk",
                    label: "Low Risk",
                    icon: "fa fa-check-circle",
                    cardClass: "investigation-card",
                    iconClass: "investigation-icon",
                },
            ];
        },

        filteredTransactions() {
            return this.transactions.filter((t) => {
                const q = this.searchQuery.toLowerCase();

                const matchQuery =
                    t.transaction_id.toLowerCase().includes(q) ||
                    t.user.toLowerCase().includes(q) ||
                    t.seller.toLowerCase().includes(q);

                const matchRisk =
                    this.riskFilter === "All" || t.risk === this.riskFilter;

                const matchStatus =
                    this.statusFilter === "All" ||
                    t.status === this.statusFilter;

                return matchQuery && matchRisk && matchStatus;
            });
        },

        paginationFrom() {
            if (this.filteredTransactions.length === 0) return 0;
            return (this.currentPage - 1) * this.perPage + 1;
        },
        paginationTo() {
            const to = this.currentPage * this.perPage;
            return Math.min(to, this.filteredTransactions.length);
        },
    },
    watch: {
        searchQuery() {
            this.currentPage = 1;
        },
        riskFilter() {
            this.currentPage = 1;
        },
        statusFilter() {
            this.currentPage = 1;
        },
    },
    methods: {
        isHashLike(str) {
            if (!str || typeof str !== "string") return false;
            return /^[a-f0-9]{32,}$/i.test(str.trim());
        },

        async fetchSuspiciousReportList() {
            try {
                const data = await this.$store.dispatch(
                    "fraudManagement/fetchSuspiciousReportList",
                    {
                        tablename: "prep_orders",
                        fromDate: moment()
                            .subtract(1, "month")
                            .format("YYYY-MM-DD"),
                        toDate: moment().format("YYYY-MM-DD"),
                        filters: {
                            orderamount: { gte: 10000 },
                        },
                    },
                );
                this.transactionData = data?.data?.result || data?.result || [];
            } catch (error) {
                console.error("Error fetching suspicious transactions:", error);
                this.transactionData = [];
            }
        },
        async fetchRiskDataMethod() {
            try {
                const toDate = new Date().toISOString().split("T")[0];
                const fromDateObj = new Date();
                fromDateObj.setMonth(fromDateObj.getMonth() - 1);

                const fromDate = fromDateObj.toISOString().split("T")[0];
                const payload = {
                    items: [
                        {
                            key: "total-amount-risk",
                            variables: {
                                tenant:
                                    this.$currentUserData.tenant ||
                                    this.$currentUserData._id,
                                fromDate: fromDate,
                                toDate: toDate,
                            },
                        },
                    ],
                };
                const data = await this.$store.dispatch(
                    "partner/fetchFraudManagementData",
                    payload,
                );
                this.riskData = data["total-amount-risk"] || {};
            } catch (error) {
                console.error("Error fetching suspicious transactions:", error);
                this.transactionData = [];
            }
        },
    },
    async mounted() {
        await this.fetchSuspiciousReportList();
        await this.fetchRiskDataMethod();
    },
};
</script>

<style scoped>
.alert-card {
    height: 80px;
    border-radius: 10px;
    background: #fff;
    display: flex;
    align-items: center;
}

.card-content {
    display: flex;
    align-items: flex-start;
}

.card-text {
    margin-left: 0.75rem;
}
.main-title {
    font-size: clamp(13px, 2.6vw, 15px);
    font-weight: 500;
    color: #222;
}

.icon-box {
    width: 38px;
    height: 38px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
}
.total-icon {
    background: #d9eaff;
    color: #4a8ef0;
}
.critical-icon {
    background: #ffdad9;
    color: #e63939;
}
.active-icon {
    background: #ffe7c3;
    color: #ff9e1f;
}
.investigation-icon {
    background: #fff3b6;
    color: #d4a733;
}

.filter-box {
    border-radius: 14px;
    background: #fff;
    border: 1px solid #e6e6e6;
}

.search-wrapper {
    position: relative;
}

.search-input {
    height: 44px !important;
    padding-left: 38px !important;
    padding-right: 38px !important;
    border-radius: 12px !important;
    border: 1px solid #e6e6e6 !important;
    background: #fff !important;
    box-sizing: border-box !important;
    line-height: 44px !important;
}

.search-icon {
    position: absolute;
    top: 50%;
    left: 12px;
    transform: translateY(-50%);
    color: #909090;
    font-size: 14px;
}

.custom-select-box {
    height: 44px;
    border-radius: 12px !important;
    border: 1px solid #e6e6e6 !important;
    background-color: #fff !important;
    font-size: 14px;
    padding-left: 12px;
}

.transaction-table td {
    vertical-align: middle;
}

.risk-badge {
    padding: 4px 12px;
    border-radius: 14px;
    color: #fff;
    font-size: 12px;
    display: inline-block;
    min-width: 85px;
    text-align: center;
}
.risk-high {
    background: #ff5a5a;
}
.risk-medium {
    background: #ffa937;
}
.risk-low {
    background: #4caf50;
}
.risk-na {
    color: black !important;
}

.flag-icon {
    margin-right: 6px;
}

.status-badge {
    padding: 4px 12px;
    border-radius: 14px;
    font-size: 12px;
    white-space: nowrap;
    display: inline-block;
}

.status-flagged {
    background: #ffe2e2;
    color: #e63946;
}

.status-underreview {
    background: #fff3cd;
    color: #b8860b;
}

.status-cleared {
    background: #d4f7d4;
    color: #256029;
}

.pagination-wrapper {
    display: flex;
    justify-content: center;
    margin-top: 1rem;
}

@media (min-width: 768px) {
    .pagination-wrapper {
        justify-content: flex-end;
    }
}

@media (max-width: 767.98px) {
    .alert-card {
        margin-top: 1rem;
        padding: 1rem !important;
    }

    .filter-box {
        padding: 1rem !important;
    }

    .search-wrapper {
        margin-bottom: 1rem;
    }

    .custom-select-box {
        margin-bottom: 0.5rem;
    }

    .transaction-table {
        font-size: 12px;
    }

    .transaction-table td,
    .transaction-table th {
        padding: 8px 10px !important;
    }
}
</style>
